/**
 * DashboardTab — executive dashboard cho Production Report module
 * (spec §8 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`).
 *
 * Visual (chốt với chị Đẹp 2026-06-02):
 *  - "Tinted tiles": KPI tiles nền màu nhạt theo accent + icon chip; Value
 *    Extracted = tile xanh/đỏ theo dấu. Summary gom 4 tile: Net weight
 *    (Input/Output) + Total value (In/Out) + Value extracted + Yield
 *    (Out-turn/Loss) — dual tile 2 chỉ số cùng nhóm.
 *  - Charts = @mui/x-charts (Pie/Bar): tooltip hover, trục/grid, legend,
 *    animation — đồng bộ theme MUI. Chart cards bo góc + shadow + dot màu.
 *
 * Data 100% LIVE từ `GET /factories/{fc}/batch-report/{po}/dashboard`
 * (aggregation 2 view). KHÔNG mock/dummy: chưa chọn PO → empty; loading →
 * spinner; lỗi → Alert ([[feedback_no_silent_tenant_default]]).
 *
 * ⚠ Value$ (KPI value-in/out + 2 chart "Value by Category" — Excel BK/BO) phụ
 * thuộc cột `value_money` của view, chỉ có khi đã wire Value$
 * ([[project_batch_report_output_view]]); chưa wire → hiện 0 / empty-state.
 */
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded'
import ScaleRoundedIcon from '@mui/icons-material/ScaleRounded'
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { BarChart } from '@mui/x-charts/BarChart'
import { PieChart } from '@mui/x-charts/PieChart'
import type { ReactNode } from 'react'

import { useGetBatchReportDashboardQuery } from '../../../../store/api/factoryBomApi'
import { BlockTitle } from './reportUi'
import { COLORS } from './styleTokens'

interface Props {
  factoryCode: string
  poNo: string
  poType: string
}

/* ---------- palette (tone TLC) ------------------------------------------- */
// Toàn bộ màu lấy từ bộ thương hiệu TLC (theme/index.ts): teal #145a59 /
// forest #0b3633 / copper #da833c / harvest gold / signal red #bc2434 /
// success #2f8f63 — KHÔNG dùng tím/xanh dương lệch brand.
// Accent KPI tile — 4 tông TLC phân biệt.
const ACCENT = {
  input: '#145a59', // teal (primary)
  output: '#2f8f63', // success green
  valueIn: '#da833c', // copper
  valueOut: '#b8902f', // harvest gold (đậm cho dễ đọc)
} as const
// Bảng màu THỐNG NHẤT theo category — dùng chung doughnut + bar items để 1
// category luôn 1 màu khắp dashboard. Semantic trong tông TLC: Loss đỏ, Graded
// teal (sản phẩm chính), By-products copper, Rejects gold, FAQ forest.
const CATEGORY_COLORS: Record<string, string> = {
  Graded: '#157f76', // teal sáng (brand)
  'By-products': '#da833c', // copper
  Rejects: '#caa12e', // harvest gold đậm
  Loss: '#bc2434', // signal red
  FAQ: '#2a5b56', // forest-teal (input)
}
const FALLBACK_CAT_COLOR = '#8a948f' // muted sage — category lạ / rỗng
const catColor = (c: string | undefined) => CATEGORY_COLORS[c ?? ''] ?? FALLBACK_CAT_COLOR
// Series phụ (BOM / Input) — sage nhạt trung tính tương phản accent chính.
const MUTED_SERIES = '#cdd8d3'

/* ---------- formatters --------------------------------------------------- */

function fmtMt(v: number): string {
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtPct(v: number): string {
  return (v * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%'
}
function fmtUsd(v: number): string {
  return '$ ' + Math.round(v).toLocaleString('en-US')
}
function fmtIntSigned(v: number): string {
  return (v >= 0 ? '+' : '−') + Math.round(Math.abs(v)).toLocaleString('en-US')
}
// Signed với 2 số thập phân (card "Value extracted ($)" — Total + Per Mt).
function fmtSigned2(v: number): string {
  return (v >= 0 ? '+' : '−') + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const pctFmt = (v: number | null) => (v == null ? '' : v.toFixed(2) + '%')
const mtFmt = (v: number | null) => (v == null ? '' : fmtMt(v) + ' Mt')
const usdFmt = (v: number | null) => (v == null ? '' : fmtUsd(v))

// Trục/nhãn x-charts dùng chung.
const AXIS_LABEL_STYLE = { fontSize: 11, fill: COLORS.textMuted } as const
const CHART_SX = {
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: COLORS.border },
  '& .MuiChartsGrid-line': { stroke: COLORS.border, strokeDasharray: '3 3' },
} as const

const VALUE_NOTE_SX = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: COLORS.text,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
} as const

function formatBarValue(valueFormatter: (v: number | null) => string, v: number | null | undefined) {
  if (v == null || v === 0) return ''
  return valueFormatter(v)
}

/** Cột ghi chú giá trị bên phải chart — căn theo band row của x-charts. */
function BarValueNotes({
  rowCount,
  chartHeight,
  marginTop,
  marginBottom,
  rows,
  minWidth = 56,
}: {
  rowCount: number
  chartHeight: number
  marginTop: number
  marginBottom: number
  rows: ReactNode[]
  minWidth?: number
}) {
  const plotHeight = chartHeight - marginTop - marginBottom
  const rowH = plotHeight / Math.max(rowCount, 1)
  return (
    <Stack
      sx={{
        flexShrink: 0,
        minWidth,
        pt: `${marginTop}px`,
        pb: `${marginBottom}px`,
        height: chartHeight,
        boxSizing: 'border-box',
      }}
    >
      {rows.map((row, i) => (
        <Box
          key={i}
          sx={{ height: rowH, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', gap: 0.2 }}
        >
          {row}
        </Box>
      ))}
    </Stack>
  )
}

/* ---------- KPI tile (tinted) -------------------------------------------- */

const tileSx = (accent: string) =>
  ({
    flex: '1 1 0',
    minWidth: 200,
    border: `1px solid ${alpha(accent, 0.22)}`,
    borderRadius: 2,
    bgcolor: alpha(accent, 0.07),
    px: 2,
    py: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
    transition: 'box-shadow 150ms ease, transform 150ms ease',
    '&:hover': { boxShadow: `0 8px 20px ${alpha(accent, 0.18)}`, transform: 'translateY(-2px)' },
  }) as const

function TileHeader({ label, accent, icon }: { label: string; accent: string; icon: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(accent, 0.16), color: accent,
          '& svg': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
    </Stack>
  )
}

/* ---------- Dual KPI tile — 2 chỉ số cùng nhóm trong 1 tile --------------- */

interface DualKpiTileProps {
  label: string
  accent: string
  icon: ReactNode
  items: { label: string; value: string; color?: string }[]
}

function DualKpiTile({ label, accent, icon, items }: DualKpiTileProps) {
  return (
    <Box sx={tileSx(accent)}>
      <TileHeader label={label} accent={accent} icon={icon} />
      <Stack direction="row" spacing={2} divider={<Box sx={{ width: '1px', bgcolor: alpha(accent, 0.2) }} />}>
        {items.map((it) => (
          <Box key={it.label} sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.66rem', color: COLORS.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {it.label}
            </Typography>
            <Typography
              sx={{ fontSize: '1.25rem', fontWeight: 800, color: it.color ?? COLORS.text, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}
            >
              {it.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

/* ---------- chart card --------------------------------------------------- */

interface ChartCardProps {
  title: string
  accent: string
  children: ReactNode
  subtitle?: string
}

function ChartCard({ title, accent, children, subtitle }: ChartCardProps) {
  return (
    <Box
      sx={{
        flex: '1 1 380px',
        minWidth: 300,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 2,
        bgcolor: COLORS.bgPage,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
        transition: 'box-shadow 150ms ease',
        '&:hover': { boxShadow: '0 6px 18px rgba(16,24,40,0.08)' },
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', color: COLORS.text, letterSpacing: 0.2, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSubtle, lineHeight: 1.2 }}>{subtitle}</Typography>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ p: 1, display: 'flex', alignItems: 'stretch', width: '100%', minWidth: 0 }}>{children}</Box>
    </Box>
  )
}

/* ---------- empty-state (chart không có dữ liệu) ------------------------- */

function ChartEmpty({ height, message }: { height: number; message: string }) {
  return (
    <Box sx={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
      <Typography sx={{ fontSize: '0.8rem', color: COLORS.textSubtle, textAlign: 'center' }}>{message}</Typography>
    </Box>
  )
}

/* ---------- Pie donut (Category %) — x-charts ---------------------------- */

function PieDonut({ data, height = 210 }: { data: { category: string; mt: number }[]; height?: number }) {
  const total = data.reduce((s, d) => s + d.mt, 0)
  if (total <= 0) return <ChartEmpty height={height} message="No output recorded for this PO." />
  const slicePct = (v: number) => `${((v / total) * 100).toFixed(1)}%`
  const outerRadius = height / 2 - 8
  const innerRadius = Math.round(outerRadius * 0.52)
  const series = [
    {
      data: data.map((d, i) => ({ id: i, value: d.mt, label: d.category, color: catColor(d.category) })),
      innerRadius,
      outerRadius,
      paddingAngle: 1.5,
      cornerRadius: 4,
      highlightScope: { fade: 'global', highlight: 'item' } as const,
      arcLabel: (item: { value: number }) => ((item.value / total) * 100 >= 8 ? slicePct(item.value) : ''),
      arcLabelMinAngle: 22,
      valueFormatter: (item: { value: number }) => `${fmtMt(item.value)} Mt · ${slicePct(item.value)}`,
    },
  ]
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
      {/* Chart chiếm phần còn lại; legend chỉ rộng đúng nội dung — không kéo giãn. */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <PieChart
          series={series}
          width={height}
          height={height}
          slotProps={{ legend: { hidden: true } }}
          margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
          sx={{ '& .MuiPieArcLabel-root': { fill: '#fff', fontWeight: 700, fontSize: 11 } }}
        />
      </Box>
      <Stack spacing={0.2} sx={{ flexShrink: 0, pl: 0.5, pr: 0.25 }}>
        {data.map((d) => (
          <Stack key={d.category} direction="row" spacing={0.4} alignItems="center">
            <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: catColor(d.category), flexShrink: 0 }} />
            <Typography
              component="span"
              sx={{
                fontSize: '0.68rem',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Box component="span" sx={{ color: COLORS.text }}>{d.category}</Box>
              <Box component="span" sx={{ color: COLORS.textSubtle, mx: 0.4 }}>·</Box>
              <Box component="span" sx={{ color: COLORS.textMuted, fontWeight: 600 }}>{fmtMt(d.mt)}</Box>
              <Box component="span" sx={{ color: COLORS.textSubtle, mx: 0.4 }}>·</Box>
              <Box component="span" sx={{ color: COLORS.textSubtle }}>{slicePct(d.mt)}</Box>
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

/* ---------- Horizontal bar (single series) — x-charts -------------------- */

function HBar({
  labels,
  values,
  color,
  valueFormatter,
  height,
  barColors,
}: {
  labels: string[]
  values: number[]
  color: string
  valueFormatter: (v: number | null) => string
  height: number
  // Tô màu TỪNG bar (vd theo category) — ordinal colorMap trên band axis.
  barColors?: string[]
}) {
  const marginTop = 6
  const marginBottom = 6
  const valueTexts = values.map((v) => formatBarValue(valueFormatter, v))
  const valueColW = Math.max(56, ...valueTexts.map((s) => s.length * 6.5))

  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0, gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <BarChart
          layout="horizontal"
          height={height}
          series={[{ data: values, color, valueFormatter }]}
          yAxis={[{
            scaleType: 'band',
            data: labels,
            disableLine: true,
            disableTicks: true,
            tickLabelStyle: AXIS_LABEL_STYLE,
            ...(barColors ? { colorMap: { type: 'ordinal' as const, values: labels, colors: barColors } } : {}),
          }]}
          xAxis={[{ valueFormatter, tickLabelStyle: AXIS_LABEL_STYLE }]}
          slotProps={{ legend: { hidden: true } }}
          grid={{ vertical: true }}
          borderRadius={5}
          margin={{ top: marginTop, bottom: marginBottom, left: 84, right: 8 }}
          sx={CHART_SX}
        />
      </Box>
      <BarValueNotes
        rowCount={labels.length}
        chartHeight={height}
        marginTop={marginTop}
        marginBottom={marginBottom}
        minWidth={valueColW}
        rows={valueTexts.map((text, i) => (
          text ? <Typography key={i} sx={VALUE_NOTE_SX}>{text}</Typography> : null
        ))}
      />
    </Box>
  )
}

/* ---------- Grouped horizontal bar (2 series) — x-charts ----------------- */

function GroupedHBar({
  labels,
  series,
  valueFormatter,
  height,
}: {
  labels: string[]
  series: Array<{ label: string; data: number[]; color: string }>
  valueFormatter: (v: number | null) => string
  height: number
}) {
  const marginTop = 34
  const marginBottom = 6
  const valueRows = labels.map((_, rowIdx) =>
    series.map((s) => {
      const text = formatBarValue(valueFormatter, s.data[rowIdx])
      if (!text) return null
      return (
        <Stack key={s.label} direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
          <Typography sx={VALUE_NOTE_SX}>{text}</Typography>
        </Stack>
      )
    }),
  )
  const valueColW = Math.max(
    72,
    ...series.flatMap((s) => s.data.map((v) => formatBarValue(valueFormatter, v).length * 6.5)),
  )

  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0, gap: 0.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <BarChart
          layout="horizontal"
          height={height}
          series={series.map((s) => ({ ...s, valueFormatter }))}
          yAxis={[{ scaleType: 'band', data: labels, disableLine: true, disableTicks: true, tickLabelStyle: AXIS_LABEL_STYLE }]}
          xAxis={[{ valueFormatter, tickLabelStyle: AXIS_LABEL_STYLE }]}
          grid={{ vertical: true }}
          borderRadius={4}
          // top margin chừa chỗ cho legend (Actual/BOM) — tránh legend đè lên bar.
          margin={{ top: marginTop, bottom: marginBottom, left: 84, right: 8 }}
          slotProps={{ legend: { labelStyle: { fontSize: 12 } } }}
          sx={CHART_SX}
        />
      </Box>
      <BarValueNotes
        rowCount={labels.length}
        chartHeight={height}
        marginTop={marginTop}
        marginBottom={marginBottom}
        minWidth={valueColW}
        rows={valueRows}
      />
    </Box>
  )
}

/* ---------- main tab ----------------------------------------------------- */

export default function DashboardTab({ factoryCode, poNo }: Props) {
  const { data, isFetching, isError } = useGetBatchReportDashboardQuery(
    { factoryCode, poNo },
    { skip: !poNo },
  )

  // 100% API — KHÔNG fallback dummy. Empty / loading / error tách bạch.
  if (!poNo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <Typography sx={{ fontSize: '0.85rem', color: COLORS.textMuted }}>
          Select a PO to view the dashboard.
        </Typography>
      </Box>
    )
  }
  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        Could not load dashboard data for {poNo}. Please retry or check the system connection.
      </Alert>
    )
  }
  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
        <CircularProgress size={22} />
      </Box>
    )
  }

  const k = data.kpis
  // Defensive: API cũ (backend chưa reload) có thể thiếu field mới → ?? [] để
  // KHÔNG crash khi đọc .length/.reduce ([[feedback_api_view_source_of_truth]]).
  const catDist = data.outputCategoryDistribution ?? []
  const itemsMt = data.outputItemsMt ?? []
  const catActualVsBom = data.categoryActualVsBom ?? []
  const itemActualVsBom = data.itemActualVsBom ?? []
  const qualityDefects = data.qualityDefects ?? []
  const qualityScreen = data.qualityScreen ?? []
  const valueIn = data.valueInByCategory ?? []
  const valueOut = data.valueOutByCategory ?? []

  // Heights động: chart nhiều hàng → cao hơn (page tự cuộn).
  const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))
  const itemsH = clamp(itemsMt.length * 26, 220, 460)
  const catCmpH = clamp(catActualVsBom.length * 56, 220, 360)
  const itemCmpH = clamp(itemActualVsBom.length * 40, 260, 620)
  const defectsH = clamp(qualityDefects.length * 52, 200, 360)
  const screenH = clamp(qualityScreen.length * 60, 180, 320)
  const valInH = clamp(valueIn.length * 46, 200, 320)
  const valOutH = clamp(valueOut.length * 46, 200, 320)

  const valInTotal = valueIn.reduce((s, d) => s + d.value, 0)
  const valOutTotal = valueOut.reduce((s, d) => s + d.value, 0)
  const VALUE_PENDING = 'Value$ not available yet for this PO.'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ===== Summary — KPI tiles (gồm Out-turn / Loss) ===== */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ gap: 1, mb: 0.5 }}>
          <BlockTitle>Summary</BlockTitle>
          {isFetching ? (
            <Typography sx={{ fontSize: '0.8125rem', color: COLORS.textMuted, mr: 0.5 }}>refreshing…</Typography>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <DualKpiTile
            label="Net weight (Mt)"
            accent={ACCENT.input}
            icon={<ScaleRoundedIcon />}
            items={[
              { label: 'Input', value: fmtMt(k.inputNetWeight) },
              { label: 'Output', value: fmtMt(k.outputNetWeight) },
            ]}
          />
          <DualKpiTile
            label="Total value ($)"
            accent={ACCENT.valueIn}
            icon={<PaymentsRoundedIcon />}
            items={[
              { label: 'In', value: Math.round(k.totalValueIn).toLocaleString('en-US') },
              { label: 'Out', value: Math.round(k.totalValueOut).toLocaleString('en-US') },
            ]}
          />
          <DualKpiTile
            label="Value extracted ($)"
            accent={k.valueExtracted >= 0 ? COLORS.success : COLORS.danger}
            icon={k.valueExtracted >= 0 ? <TrendingUpRoundedIcon /> : <TrendingDownRoundedIcon />}
            items={[
              {
                label: 'Total',
                value: fmtSigned2(k.valueExtracted),
                color: k.valueExtracted >= 0 ? COLORS.success : COLORS.danger,
              },
              {
                label: 'Per Mt',
                value: fmtSigned2(k.valueExtractedPerMt),
                color: k.valueExtractedPerMt >= 0 ? COLORS.success : COLORS.danger,
              },
            ]}
          />
          <DualKpiTile
            label="Yield"
            accent={COLORS.success}
            icon={<TrendingUpRoundedIcon />}
            items={[
              { label: 'Out-turn', value: fmtPct(k.outTurn), color: COLORS.success },
              { label: 'Loss', value: fmtPct(k.loss), color: COLORS.danger },
            ]}
          />
        </Stack>
      </Box>

      {/* ===== Output Breakdown — Doughnut + Items Mt ===== */}
      <Box>
        <BlockTitle>Output Breakdown</BlockTitle>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <ChartCard title="Category (%)" subtitle="Output distribution by phase" accent={catColor('Graded')}>
            <PieDonut data={catDist} />
          </ChartCard>
          <ChartCard title="Items (Mt)" subtitle="Color = category (matches donut)" accent={COLORS.primary}>
            <HBar
              labels={itemsMt.map((d) => d.item)}
              values={itemsMt.map((d) => d.mt)}
              barColors={itemsMt.map((d) => catColor(d.category))}
              color={COLORS.primary}
              valueFormatter={mtFmt}
              height={itemsH}
            />
          </ChartCard>
        </Stack>
      </Box>

      {/* ===== Actual vs BOM — Category + Item ===== */}
      <Box>
        <BlockTitle>Actual vs BOM</BlockTitle>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <ChartCard title="By Category (%)" subtitle="Actual yield vs recipe" accent={COLORS.primary}>
            <GroupedHBar
              labels={catActualVsBom.map((d) => d.label)}
              series={[
                { label: 'Actual', data: catActualVsBom.map((d) => d.actual), color: COLORS.primary },
                { label: 'BOM', data: catActualVsBom.map((d) => d.bom), color: MUTED_SERIES },
              ]}
              valueFormatter={pctFmt}
              height={catCmpH}
            />
          </ChartCard>
          <ChartCard title="By Item (%)" subtitle="Actual yield vs recipe" accent={COLORS.primary}>
            <GroupedHBar
              labels={itemActualVsBom.map((d) => d.label)}
              series={[
                { label: 'Actual', data: itemActualVsBom.map((d) => d.actual), color: COLORS.primary },
                { label: 'BOM', data: itemActualVsBom.map((d) => d.bom), color: MUTED_SERIES },
              ]}
              valueFormatter={pctFmt}
              height={itemCmpH}
            />
          </ChartCard>
        </Stack>
      </Box>

      {/* ===== Quality Comparison — Defects + Screen (Output vs Input) ===== */}
      <Box>
        <BlockTitle>Quality Comparison</BlockTitle>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <ChartCard title="Defects (Output vs Input)" subtitle="Lower output = better" accent={COLORS.success}>
            <GroupedHBar
              labels={qualityDefects.map((d) => d.label)}
              series={[
                { label: 'Output', data: qualityDefects.map((d) => d.output), color: COLORS.success },
                { label: 'Input', data: qualityDefects.map((d) => d.input), color: MUTED_SERIES },
              ]}
              valueFormatter={pctFmt}
              height={defectsH}
            />
          </ChartCard>
          <ChartCard title="Screen (Output vs Input)" subtitle="Bean size retention" accent={COLORS.success}>
            <GroupedHBar
              labels={qualityScreen.map((d) => d.label)}
              series={[
                { label: 'Output', data: qualityScreen.map((d) => d.output), color: COLORS.success },
                { label: 'Input', data: qualityScreen.map((d) => d.input), color: MUTED_SERIES },
              ]}
              valueFormatter={pctFmt}
              height={screenH}
            />
          </ChartCard>
        </Stack>
      </Box>

      {/* ===== Value by Category — Excel BK/BO (In = issue, Out = receive) ===== */}
      <Box>
        <BlockTitle>Value by Category</BlockTitle>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <ChartCard title="Value In ($)" subtitle="Input value by category" accent={ACCENT.valueIn}>
            {valInTotal > 0 ? (
              <HBar
                labels={valueIn.map((d) => d.category)}
                values={valueIn.map((d) => d.value)}
                color={ACCENT.valueIn}
                valueFormatter={usdFmt}
                height={valInH}
              />
            ) : (
              <ChartEmpty height={valInH} message={VALUE_PENDING} />
            )}
          </ChartCard>
          <ChartCard title="Value Out ($)" subtitle="Output value by category" accent={ACCENT.valueOut}>
            {valOutTotal > 0 ? (
              <HBar
                labels={valueOut.map((d) => d.category)}
                values={valueOut.map((d) => d.value)}
                color={ACCENT.valueOut}
                valueFormatter={usdFmt}
                height={valOutH}
              />
            ) : (
              <ChartEmpty height={valOutH} message={VALUE_PENDING} />
            )}
          </ChartCard>
        </Stack>
      </Box>
    </Box>
  )
}
